import { api } from './client';
import { useAuthStore } from '@/stores/auth';
import { useAnonymousSessionStore } from '@/stores/anonymousSession';
import type { WebhookEvent } from './types';

/**
 * Wire types for the Copilot endpoints. Must match
 * apps/core/src/modules/copilot/copilot.dto.ts exactly.
 *
 * Proposals come in two scopes:
 *  - backend: applied via POST /v1/copilot/proposals/apply
 *  - frontend: applied entirely in the SPA on user confirmation
 */

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

// ─────────── Backend proposals ───────────

export interface BusinessDataProposal {
  proposalId: string;
  kind: 'business_data_update';
  scope: 'backend';
  projectId: string;
  patch: Record<string, string | null>;
  rationale: string;
}

export interface ScenarioPromptProposal {
  proposalId: string;
  kind: 'scenario_prompt_update';
  scope: 'backend';
  projectId: string;
  scenarioId: string;
  newPrompt: string;
  rationale: string;
}

export interface ProjectStatusProposal {
  proposalId: string;
  kind: 'project_status_change';
  scope: 'backend';
  projectId: string;
  status: 'draft' | 'active' | 'paused';
  rationale: string;
}

export type CopilotPricingTier = 'economy' | 'standard' | 'premium' | 'enterprise';

export interface ScenarioPricingTierProposal {
  proposalId: string;
  kind: 'scenario_pricing_tier_change';
  scope: 'backend';
  projectId: string;
  scenarioId: string;
  tier: CopilotPricingTier;
  rationale: string;
}

export type SlotDataType =
  | 'string'
  | 'integer'
  | 'datetime'
  | 'enum_value'
  | 'phone'
  | 'email'
  | 'boolean';

export interface SlotInput {
  key: string;
  displayName: string;
  description?: string;
  dataType: SlotDataType;
  required?: boolean;
  enumValues?: string[];
  promptHint?: string;
}

export interface AddSlotProposal {
  proposalId: string;
  kind: 'add_slot';
  scope: 'backend';
  projectId: string;
  scenarioId: string;
  slot: SlotInput;
  rationale: string;
}

export interface CreateScenarioProposal {
  proposalId: string;
  kind: 'create_scenario';
  scope: 'backend';
  projectId: string;
  name: string;
  description?: string;
  direction: 'inbound' | 'outbound';
  goal: { type: 'all_required_slots_filled' } | { type: 'tool_called_successfully'; tool: string };
  systemPromptTemplate: string;
  maxDurationSeconds?: number;
  slots?: SlotInput[];
  rationale: string;
}

/**
 * Per ADR-0040 (philosophy §1.4 recursion). Terminal proposal of the
 * `goal_creation` Copilot mode. Mirrors the per-goal payload from
 * `goals_and_pack` (the original ADR-0034 entry point); the apply
 * path runs `ProjectGoalRepository.create` and returns the new
 * `createdId`.
 */
export interface GoalCreationProposal {
  proposalId: string;
  kind: 'goal_creation';
  scope: 'backend';
  projectId: string;
  idn: string;
  title: string;
  description: string;
  successCriteria: unknown;
  constraints?: Record<string, unknown>;
  escalationRules?: Record<string, unknown>;
  tone?: string | null;
  liveliness?: Liveliness | null;
  priority?: number;
  source: ProjectGoalSource;
  rationale: string;
}

/**
 * Per ADR-0062 (philosophy §1.4 recursion). Terminal proposal of
 * the plugin_creation Copilot mode. Apply delegates to
 * PluginConfigurationsService.create (ADR-0061) so the AI surface
 * and the form bypass produce bit-identical PluginConfiguration
 * rows.
 */
export interface PluginWiringProposal {
  proposalId: string;
  kind: 'plugin_wiring';
  scope: 'backend';
  projectId: string;
  pluginType: string;
  implementationId: string;
  label?: string;
  config: Record<string, unknown>;
  source: 'user' | 'assistant_proposed';
  rationale: string;
}

export interface ApplyIndustryPackProposal {
  proposalId: string;
  kind: 'apply_industry_pack';
  scope: 'backend';
  projectId: string;
  packIdn: string;
  packVersion: string;
  rationale: string;
}

export type CopilotScriptKind = 'intercept' | 'tool';
export type CopilotScriptScope = 'project' | 'scenario' | 'step';

export interface CreateScriptProposal {
  proposalId: string;
  kind: 'create_script';
  scope: 'backend';
  projectId: string;
  scopeKind: CopilotScriptScope;
  scopeId: string;
  name: string;
  scriptKind: CopilotScriptKind;
  interceptEvent?: string | null;
  orderIndex?: number;
  code: string;
  rationale: string;
}

export interface UpdateScriptProposal {
  proposalId: string;
  kind: 'update_script';
  scope: 'backend';
  projectId: string;
  scriptId: string;
  newCode: string;
  rationale: string;
}

export type CopilotStepKind = 'slot_collection' | 'say' | 'branch' | 'tool_call';

export type CopilotStepTransitions =
  | { kind: 'next'; nextStepIdn: string }
  | { kind: 'end_session'; outcome: 'completed' | 'agent_ended' }
  | {
      kind: 'cases';
      cases: Array<{ whenScriptIdn?: string; nextStepIdn: string }>;
      fallbackStepIdn: string;
    }
  | {
      kind: 'goal_check';
      onReached:
        | { kind: 'end_session'; outcome: 'completed' | 'agent_ended' }
        | { kind: 'next'; nextStepIdn: string };
      onUnreached: { kind: 'next'; nextStepIdn: string };
    };

export interface CreateStepProposal {
  proposalId: string;
  kind: 'create_step';
  scope: 'backend';
  projectId: string;
  scenarioId: string;
  idn: string;
  name: string;
  stepKind: CopilotStepKind;
  promptFragment?: string;
  transitions: CopilotStepTransitions;
  orderIndex?: number;
  rationale: string;
}

export interface UpdateStepProposal {
  proposalId: string;
  kind: 'update_step';
  scope: 'backend';
  projectId: string;
  stepId: string;
  patch: {
    idn?: string;
    name?: string;
    stepKind?: CopilotStepKind;
    promptFragment?: string;
    transitions?: CopilotStepTransitions;
    orderIndex?: number;
  };
  rationale: string;
}

// ─────────── Frontend proposals ───────────

export type NavigateTarget =
  | 'dashboard'
  | 'quickstart'
  | 'project_overview'
  | 'project_agent'
  | 'project_calls'
  | 'project_connections'
  | 'project_settings'
  // Per ADR-0040 §1.4 recursion. AI-first goal-creation surface.
  | 'project_goal_new'
  // Per ADR-0062 §1.4 recursion. AI-first plugin-wiring surface.
  | 'project_plugin_new';

export interface NavigateProposal {
  proposalId: string;
  kind: 'navigate';
  scope: 'frontend';
  target: NavigateTarget;
  projectId?: string;
  rationale: string;
}

export interface SetThemeProposal {
  proposalId: string;
  kind: 'set_theme';
  scope: 'frontend';
  theme: 'light' | 'dark' | 'auto';
  rationale: string;
}

export interface ToggleDeveloperModeProposal {
  proposalId: string;
  kind: 'toggle_developer_mode';
  scope: 'frontend';
  enabled: boolean;
  rationale: string;
}

// ─────────── Goals dialog (per ADR-0033 + ADR-0034) ───────────

export type ProjectGoalSource = 'user' | 'assistant_proposed';

/**
 * Per ADR-0048. Graduated voice register: 0 = formal, 1 = neutral
 * (default-effective), 2 = warm. The Goals dialog records this alongside
 * `tone` (verbatim sample utterance) so the conversations engine can
 * pick the right Voice block at session start.
 */
export type Liveliness = 0 | 1 | 2;

export interface ProposedGoal {
  idn: string;
  title: string;
  description: string;
  successCriteria: unknown;
  constraints: Record<string, unknown>;
  escalationRules: Record<string, unknown>;
  tone: string;
  /** Per ADR-0048. Voice register for this goal. */
  liveliness: Liveliness;
  priority: number;
  source: ProjectGoalSource;
  rationale: string;
}

export interface MinimumRunnableScenarioPlan {
  summary: string;
  scenarioTitle: string;
  scenarioIdn?: string;
  primaryGoal: string;
  conversationPhases: string[];
  requiredCustomerInformation: string[];
  requiredToolsOrIntegrations: string[];
  fallbackAndEscalation: string;
  expectedOutcome: string;
  sampleDialog: {
    exchange: { role: 'caller' | 'agent'; text: string }[];
    exercises: string[];
  };
}

export interface CreatorIntegrationSetup {
  reportingEmail?: string;
  webhook?: {
    enabled: true;
    url: string;
    events: WebhookEvent[];
  };
}

export interface CreatorIntegrationWarning {
  code:
    | 'notification_email_save_failed'
    | 'webhook_create_failed'
    | 'webhook_already_exists'
    | 'setup_email_not_configured'
    | 'setup_email_send_failed';
  message: string;
}

export interface CreatorIntegrationSetupResult {
  reportingEmail?: string;
  notificationEmailSaved?: boolean;
  webhook?: {
    id: string;
    projectId?: string;
    url: string;
    events: WebhookEvent[];
    enabled?: boolean;
    signingSecret?: string;
    secretAvailable?: boolean;
  };
  setupEmailSent?: boolean;
  warnings?: CreatorIntegrationWarning[];
}

export interface ApplyBackendProposalResult {
  ok: true;
  kind: string;
  createdId?: string;
  integrationSetup?: CreatorIntegrationSetupResult;
}

export interface ApplyBackendProposalOptions {
  conversationId?: string;
  proposalId?: string;
}

export interface GoalsAndPackProposal {
  proposalId: string;
  kind: 'goals_and_pack';
  scope: 'backend';
  projectId: string;
  goals: ProposedGoal[];
  packIdn: string | null;
  packVersion: string | null;
  rationale: string;
  minimumRunnableScenarioPlan: MinimumRunnableScenarioPlan;
  integrationSetup?: CreatorIntegrationSetup;
}

export interface ClarificationQuestionProposal {
  proposalId: string;
  kind: 'clarification_question';
  scope: 'frontend';
  question: string;
  suggestions: string[];
  rationale: string;
}

/**
 * Per docs/19-onboarding.md (simulated-call preview). The assistant emits a
 * 4-12 turn caller↔agent dialog generated from the draft goals so the user
 * can SEE how the agent will sound BEFORE applying propose_goals_and_pack.
 * The SPA renders the exchange as alternating chat bubbles inside the
 * assistant's turn. The user reacts in chat — no apply button on this card.
 */
export interface SimulatedCallTranscriptProposal {
  proposalId: string;
  kind: 'simulated_call_transcript';
  scope: 'frontend';
  exchange: { role: 'caller' | 'agent'; text: string }[];
  rationale: string;
  /** Per ADR-0047. Optional pair grouping. When two proposals share a
   *  pairId with variants `before` / `after`, the SPA renders them
   *  side-by-side as one comparison card. */
  variant?: 'standalone' | 'before' | 'after';
  pairId?: string;
  /** One-line caller persona description, rendered above the transcript. */
  callerProfile?: string;
  /** 1–3 specific things this call exercises, rendered as chips. */
  exercises?: string[];
}

/**
 * Per ADR-0047 §"Explain-in-reverse". Composite "what does this do?"
 * proposal: a business-language summary of the artifact plus a
 * standalone simulated call demonstrating its behaviour. The SPA
 * renders the summary as a paragraph and reuses the existing
 * simulated-call bubble strip below. `general` mode only.
 */
export interface ArtifactWalkthroughProposal {
  proposalId: string;
  kind: 'artifact_walkthrough';
  scope: 'frontend';
  artifactType: 'scenario' | 'goal' | 'project' | 'plugin_instance';
  artifactId: string;
  businessSummary: string;
  /** Nested simulated-call payload. `variant` is pinned to 'standalone'
   *  for this composite (use propose_simulated_call_transcript for
   *  before/after pairs). The nested object omits `proposalId` — it's
   *  not a top-level proposal on its own. */
  simulatedCall: Omit<SimulatedCallTranscriptProposal, 'proposalId'>;
}

/**
 * Per ADR-0049 (philosophy §2.5). System-emitted card carrying a real
 * test-call's transcript + outcome. Posted by the refinement subscriber
 * into the active goals_dialog conversation when a user phones the
 * default agent. The SPA renders it as a chat bubble with the transcript
 * inline; the user replies in chat to refine the agent.
 */
export interface TestCallSummaryProposal {
  proposalId: string;
  kind: 'test_call_summary';
  scope: 'frontend';
  sessionId: string;
  durationMs: number;
  goalAchieved: boolean | null;
  exchange: { role: 'caller' | 'agent' | 'system'; text: string }[];
  rationale: string;
}

export type BackendProposal =
  | BusinessDataProposal
  | ScenarioPromptProposal
  | ProjectStatusProposal
  | ScenarioPricingTierProposal
  | AddSlotProposal
  | CreateScenarioProposal
  | GoalCreationProposal
  | PluginWiringProposal
  | ApplyIndustryPackProposal
  | CreateScriptProposal
  | UpdateScriptProposal
  | CreateStepProposal
  | UpdateStepProposal
  | GoalsAndPackProposal;

export type FrontendProposal =
  | NavigateProposal
  | SetThemeProposal
  | ToggleDeveloperModeProposal
  | ClarificationQuestionProposal
  | SimulatedCallTranscriptProposal
  | ArtifactWalkthroughProposal
  | TestCallSummaryProposal;

/**
 * Server-enriched fields the SSE event attaches to every proposal. They
 * are typed as optional on the proposal union so the SPA can read them
 * off `ProposalState.proposal` without juggling the SSE-event wrapper
 * type separately. Older messages persisted before §2.3/§2.7 may lack
 * these fields.
 */
export interface ProposalEnrichment {
  /** Per ADR-0043 / §2.3. */
  stakesTier?: ProposalStakesTier;
  /** Per ADR-0045 / §2.7. */
  forecast?: ProposalForecast | null;
}

export type Proposal = (BackendProposal | FrontendProposal) & ProposalEnrichment;

/**
 * Per ADR-0043 / philosophy §2.3. The server tags every proposal with a
 * stakes tier:
 *   - `low` — the SPA may silent-apply with an inline Undo affordance.
 *   - `high` — the SPA must render the proposal as an explicit diff
 *     card that the user accepts. The `stakesTier` field is required on
 *     the wire and forwarded with the proposal so the SPA can route
 *     each kind to the right surface without re-classifying.
 */
export type ProposalStakesTier = 'high' | 'low';

/**
 * Per ADR-0045 v1 + ADR-0063 v2 / philosophy §2.7. Cost-and-risk
 * forecast the server attaches to a proposal. v2 ships **numeric
 * `perCallCentsDelta`** (USD cents, can be negative) + an explicit
 * `confidence` band; v1 directions are retired. `null` on either arm
 * (or the whole envelope) means "no measurable insight" — the SPA
 * hides that part of the strip rather than synthesizing a placeholder.
 */
export type ForecastConfidence = 'low' | 'medium' | 'high';

export type ForecastRiskDirection = 'no_change' | 'increased_refusal' | 'reduced_refusal';

export interface ProposalForecast {
  cost: {
    perCallCentsDelta: number;
    confidence: ForecastConfidence;
    rationale: string;
  } | null;
  risk: { direction: ForecastRiskDirection; rationale: string } | null;
}

export type CopilotEvent =
  | { kind: 'conversation_created'; conversationId: string; title: string }
  | { kind: 'text_delta'; delta: string }
  | { kind: 'tool_running'; tool: string }
  | {
      kind: 'proposal';
      proposal: Proposal & {
        stakesTier?: ProposalStakesTier;
        /** Per ADR-0045. Null when the server has no causal model. */
        forecast?: ProposalForecast | null;
      };
    }
  | {
      kind: 'usage';
      inputTokens: number;
      outputTokens: number;
      cacheReadTokens: number;
      cacheWriteTokens: number;
    }
  | {
      kind: 'error';
      message: string;
      code?: string;
      details?: Record<string, unknown>;
    }
  | { kind: 'done' };

export interface UsageInfo {
  used: number;
  limit: number;
  resetAt: string;
  enforced: boolean;
}

export async function getCopilotUsage(): Promise<UsageInfo> {
  const { data } = await api.get<UsageInfo>('/v1/copilot/usage');
  return data;
}

export interface UiState {
  /** Stable route id (snake_case, see `composables/useUiState.ts`).
   *  The Copilot reasons about it programmatically; pair with `screen`
   *  for user-facing copy. */
  route?: string;
  /** Per ADR-0044 / philosophy §1.5. Business-language label for the
   *  current screen ("Редактирование сценария"). Filled by the SPA from
   *  `i18n.screens.<route>`; absent when a route hasn't been labelled. */
  screen?: string;
  activeTab?: string;
  /** Project the user is currently inside, when scoped. Lets the
   *  Copilot answer "this project" without parsing the route. */
  projectId?: string;
  scenarioId?: string;
  sessionId?: string;
  /** Per CLAUDE.md "English is the primary product language" cascade.
   *  BCP-47 locale of the visitor's current SPA / marketing session
   *  (`en-US`, `ru-RU`, `es-ES`). The Copilot uses it as a tier-3 fall-
   *  back when the user hasn't written yet and the project has no
   *  defaultLanguage. Without it, the model picks the wrong language
   *  for short / ambiguous first turns (observed 2026-05-16: English-
   *  locale visitor got a Russian-speaking Copilot). */
  clientLocale?: string;
}

export interface StreamMessagesBody {
  /** When omitted, the server creates a fresh conversation and emits a
   *  `conversation_created` SSE event before any text delta. */
  conversationId?: string;
  projectId?: string;
  uiState?: UiState;
  /** Per ADR-0041 / philosophy §1.1. Which channel the user is currently
   *  on for this turn. Stamped on the persisted CopilotMessage row. */
  channel?: 'text' | 'voice';
  /** The new user turn — required. */
  newUserMessage: string;
  /** Structured UI action from proposal cards. Text remains the visible
   *  chat/audit surface; this carries state-machine intent for flows like
   *  accepting the latest simulated call preview. */
  action?: {
    proposalKind: string;
    proposalId?: string;
    action: 'accept' | 'revise';
  };
}

export interface ConversationSummary {
  id: string;
  projectId: string | null;
  /** Mirrors `copilotConversation.mode` on the wire so mode-specific
   *  pages (e.g. GoalsDialogPage) can pick the right conversation out
   *  of `listConversations({ projectId })` instead of letting the next
   *  `streamCopilotMessages` mint a fresh `general`-mode one and lose
   *  the server-side seeded context. */
  mode: string;
  title: string;
  createdAt: string;
  updatedAt: string;
}

export interface PersistedMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  sequence: number;
  proposals: Proposal[] | null;
  createdAt: string;
}

export interface ConversationDetail extends ConversationSummary {
  messages: PersistedMessage[];
}

/**
 * Streams a copilot turn via Server-Sent Events. We can't use EventSource
 * (GET-only, no body, no auth headers), so we drive `fetch` + a manual SSE
 * parser over the streaming response body.
 */
export interface StreamHandle {
  events: AsyncIterable<CopilotEvent>;
  cancel: () => void;
}

export function streamCopilotMessages(body: StreamMessagesBody): StreamHandle {
  const controller = new AbortController();
  const auth = useAuthStore();
  const anon = useAnonymousSessionStore();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  };
  // Per ADR-0073. Mirror the axios interceptor precedence (api/client.ts):
  // engagement token wins over JWT when an anonymous session is active, so
  // a previously signed-in visitor who chose "continue as anonymous test"
  // in the onboarding modal still drives Copilot against the pending
  // tenant — not their real one. SSE bypasses axios, so the same rule has
  // to live here verbatim.
  if (anon.engagementToken) {
    headers.Authorization = `Bearer ${anon.engagementToken}`;
  } else if (auth.accessToken) {
    headers.Authorization = `Bearer ${auth.accessToken}`;
  } else if (auth.devShim) {
    headers['x-tenant-id'] = auth.devShim.tenantId;
    headers['x-user-id'] = auth.devShim.userId;
  }

  const responsePromise = fetch('/v1/copilot/messages', {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    signal: controller.signal,
  });

  return {
    events: parseStream(responsePromise, controller),
    cancel: () => controller.abort(),
  };
}

/** Max time to response headers (server accepts the POST but never
 *  answers). Generous: the copilot server buffers goals_dialog output,
 *  so headers may take a moment behind slow LLM startup. */
const CONNECT_TIMEOUT_MS = 30_000;

/** Max silence between received bytes. The copilot SSE writes a `: hb`
 *  heartbeat every 15 s, so 40 s of silence means a dead connection —
 *  without this, `sending=true` bricks the composer until reload
 *  because the finally that resets it needs the stream to END. */
const READ_WATCHDOG_MS = 40_000;

const STREAM_STALLED_MESSAGE = 'stream stalled: no bytes received';

async function* parseStream(
  responsePromise: Promise<Response>,
  controller: AbortController,
): AsyncGenerator<CopilotEvent> {
  let response: Response;
  try {
    response = await raceTimeout(responsePromise, CONNECT_TIMEOUT_MS);
  } catch (err: unknown) {
    controller.abort();
    yield { kind: 'error', message: err instanceof Error ? err.message : String(err) };
    yield { kind: 'done' };
    return;
  }
  if (!response.ok) {
    let message = `HTTP ${response.status}`;
    try {
      const text = await response.text();
      try {
        const parsed = JSON.parse(text) as { error?: { message?: string } };
        if (parsed.error?.message) message = parsed.error.message;
        else if (text) message = text;
      } catch {
        if (text) message = text;
      }
    } catch {
      // ignore
    }
    yield { kind: 'error', message };
    yield { kind: 'done' };
    return;
  }
  if (!response.body) {
    yield { kind: 'error', message: 'Stream has no body' };
    yield { kind: 'done' };
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let stalled = false;

  try {
    while (true) {
      // Heartbeat comments count as liveness — they arrive as bytes and
      // reset the watchdog even though parseFrame drops them.
      let read: ReadableStreamReadResult<Uint8Array>;
      try {
        read = await raceTimeout(reader.read(), READ_WATCHDOG_MS);
      } catch {
        // Abort AFTER the timeout rejects, not inside the race: aborting
        // the reader mid-race can resolve read() as {done:true} and let
        // it win the race, silently swallowing the stall.
        stalled = true;
        controller.abort();
        yield { kind: 'error', message: STREAM_STALLED_MESSAGE };
        yield { kind: 'done' };
        break;
      }
      const { value, done } = read;
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let idx: number;
      while ((idx = buffer.indexOf('\n\n')) !== -1) {
        const rawFrame = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        const frame = parseFrame(rawFrame);
        if (frame) yield frame;
      }
    }
    // Flush a trailing frame that arrived without the final `\n\n` —
    // typically the server's last `error`/`done` before the socket
    // closed. Dropping it left the turn ending on an empty bubble with
    // no explanation.
    if (!stalled && buffer.trim().length > 0) {
      const frame = parseFrame(buffer);
      if (frame) yield frame;
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // already released / aborted
    }
  }
}

/** Reject after `ms` if `promise` hasn't settled. Pure — no side effects
 *  on timeout, so the losing `reader.read()` stays pending and can't
 *  win the race by resolving {done:true} when the caller aborts. */
async function raceTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<never>((_resolve, reject) => {
        timer = setTimeout(() => reject(new Error(STREAM_STALLED_MESSAGE)), ms);
      }),
    ]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

function parseFrame(raw: string): CopilotEvent | null {
  let dataLine = '';
  for (const line of raw.split('\n')) {
    if (line.startsWith(':')) continue;
    if (line.startsWith('data: ')) dataLine += line.slice(6);
    else if (line.startsWith('data:')) dataLine += line.slice(5);
  }
  if (!dataLine) return null;
  try {
    return JSON.parse(dataLine) as CopilotEvent;
  } catch {
    return null;
  }
}

export async function applyBackendProposal(
  proposal: BackendProposal,
  opts: ApplyBackendProposalOptions = {},
): Promise<ApplyBackendProposalResult> {
  const { proposalId, ...wire } = proposal;
  const body: {
    proposal: typeof wire;
    conversationId?: string;
    proposalId?: string;
  } = {
    proposal: wire,
  };
  if (opts.conversationId) body.conversationId = opts.conversationId;
  if (opts.proposalId ?? proposalId) body.proposalId = opts.proposalId ?? proposalId;
  const { data } = await api.post<ApplyBackendProposalResult>('/v1/copilot/proposals/apply', body);
  return data;
}

// ─────────── Conversation CRUD ───────────

export async function listConversations(
  opts: { projectId?: string; limit?: number } = {},
): Promise<ConversationSummary[]> {
  const params: Record<string, string | number> = {};
  if (opts.projectId) params.projectId = opts.projectId;
  if (opts.limit) params.limit = opts.limit;
  const { data } = await api.get<{ data: ConversationSummary[] }>('/v1/copilot/conversations', {
    params,
  });
  return data.data;
}

export async function getConversation(
  id: string,
  opts: { projectId?: string; mode?: 'goals_dialog' } = {},
): Promise<ConversationDetail> {
  const params: Record<string, string> = {};
  if (opts.projectId) params.projectId = opts.projectId;
  if (opts.mode) params.mode = opts.mode;
  const { data } = await api.get<ConversationDetail>(`/v1/copilot/conversations/${id}`, {
    params,
  });
  return data;
}

/**
 * Per ADR-0048. Used by user-initiated modes (`scenario_creation` today, more
 * to come). Server-managed modes (`goals_dialog`, `goal_refinement`,
 * `test_call_refinement`) are NOT openable through this endpoint — they
 * require seeded state created by their respective backend services.
 */
export async function createConversation(input: {
  projectId?: string;
  title?: string;
  // `goal_creation` per ADR-0040 §1.4 — the AI-first surface for adding
  // a new ProjectGoal. Server validates the mode; the SPA passes it
  // through from GoalCreationPage.
  mode?: 'general' | 'scenario_creation' | 'goal_creation' | 'plugin_creation';
}): Promise<ConversationSummary> {
  const { data } = await api.post<ConversationSummary>('/v1/copilot/conversations', input);
  return data;
}

/**
 * Per ADR-0044 / philosophy §1.5. Resolves the user's single
 * `mode='general'` conversation (the "one assistant identity" anchor).
 * The server returns the existing thread or creates one on first call,
 * so the SPA can drop its localStorage-first caching in favor of a
 * cross-device, server-of-record thread id.
 */
export async function fetchCurrentGeneralConversation(): Promise<ConversationSummary> {
  const { data } = await api.get<ConversationSummary>('/v1/copilot/conversations/current-general');
  return data;
}

export async function renameConversation(id: string, title: string): Promise<ConversationSummary> {
  const { data } = await api.patch<ConversationSummary>(`/v1/copilot/conversations/${id}`, {
    title,
  });
  return data;
}

export async function deleteConversation(id: string): Promise<void> {
  await api.delete(`/v1/copilot/conversations/${id}`);
}

/**
 * Per ADR-0041 / philosophy §1.1. Persists the user's channel preference
 * (first-run pick + every header-toggle flip). Returns the refreshed
 * MeResponse so callers can update the auth store with the new value
 * in one round-trip.
 */
export interface SetAssistantChannelResponse {
  id: string;
  email: string;
  displayName: string | null;
  tenantId: string;
  platformAdmin: boolean;
  assistantChannel: 'text' | 'voice' | null;
}

export async function setAssistantChannel(
  channel: 'text' | 'voice',
): Promise<SetAssistantChannelResponse> {
  const { data } = await api.patch<SetAssistantChannelResponse>('/v1/me/assistant-channel', {
    channel,
  });
  return data;
}
